import { gql } from '@apollo/client';

/**
 * Staff proxy operations — staff-side facades over 6 customer-side ops on
 * open-api. The staff-api resolver resolves the target account from the
 * input device(s), enforces single-account, then forwards to open-api with
 * `Authorization: Bearer OPS_STAFF_API_BACKEND_TOKEN` + `X-Impersonate-Account-Id`
 * + `X-Impersonate-Team-Id`.
 *
 * IMPORTANT: signatures match staff-api inputs (see
 * apps/api/src/app/proxy/proxy.inputs.ts), NOT the customer-side ops. Staff
 * unstable-shape mutations accept a `payloadJson: String` instead of the
 * full input type, so staff-api isn't coupled to open-api enum churn. The
 * `MdmActionHandlers` dispatch wrapper does the JSON.stringify on the way
 * in and JSON.parse on the way back.
 */

// mdmCommand: dedicated input (deviceIds + command + value), returns Boolean.
export const STAFF_PROXY_MDM_COMMAND = gql`
  mutation StaffProxyMdmCommand($payload: MdmCommandInput!) {
    staffProxyMdmCommand(payload: $payload)
  }
`;

// toggleDeviceFeature: deviceIds + payloadJson, returns Boolean.
export const STAFF_PROXY_TOGGLE_DEVICE_FEATURE = gql`
  mutation StaffProxyToggleDeviceFeature($payload: StaffToggleDeviceFeatureInput!) {
    staffProxyToggleDeviceFeature(payload: $payload)
  }
`;

// addCommandExecution: teamId + payloadJson + isNeedResponse, returns JSON String.
export const STAFF_PROXY_ADD_COMMAND_EXECUTION = gql`
  mutation StaffProxyAddCommandExecution($payload: StaffAddCommandExecutionInput!) {
    staffProxyAddCommandExecution(payload: $payload)
  }
`;

// addCommandExecutionGroup: teamId + payloadJson, returns JSON String.
export const STAFF_PROXY_ADD_COMMAND_EXECUTION_GROUP = gql`
  mutation StaffProxyAddCommandExecutionGroup($payload: StaffAddCommandExecutionGroupInput!) {
    staffProxyAddCommandExecutionGroup(payload: $payload)
  }
`;

// platformApiCommand: deviceId + operation + argsJson, returns JSON String.
export const STAFF_PROXY_PLATFORM_API_COMMAND = gql`
  mutation StaffProxyPlatformApiCommand($payload: StaffPlatformApiCommandInput!) {
    staffProxyPlatformApiCommand(payload: $payload)
  }
`;

// getRemoteControlLink: teamId + deviceId, returns JSON String. QUERY, not mutation
// — staff-api side keeps it as a query because there's no state change.
export const STAFF_PROXY_GET_REMOTE_CONTROL_LINK = gql`
  query StaffProxyGetRemoteControlLink($payload: StaffGetRemoteControlLinkInput!) {
    staffProxyGetRemoteControlLink(payload: $payload)
  }
`;

// pushToScreens: assign content (asset/playlist/schedule, auto-detected) to a
// device and push it to the screen via open-api. Returns JSON String result.
export const STAFF_PROXY_PUSH_TO_SCREENS = gql`
  mutation StaffProxyPushToScreens($deviceId: String!, $contentId: String!) {
    staffProxyPushToScreens(deviceId: $deviceId, contentId: $contentId)
  }
`;

// Live device heartbeat/HDMI status from open-api. Returns JSON String:
// [{ key: uuid, value, type }] — type 0 offline, 1 online, 2 HDMI off.
export const STAFF_PROXY_DEVICE_HEARTBEATS = gql`
  query StaffProxyDeviceHeartbeats($uuids: [String!]!) {
    staffProxyDeviceHeartbeats(uuids: $uuids)
  }
`;

// Device drawer reads (My OptiSigns Testing Devices + the customer Devices page). Each takes an
// OPTIONAL `$accountId`:
//  - omitted → testing gate: 403 ("Please pair your testing device to OptiSigns Testing account") for a
//    device outside the OptiSigns Testing account, so My Devices only issues them for isTestingDevice rows;
//  - passed  → customer gate: 403 ("Device does not belong to this customer") unless the device is on
//    that account. Only pass it from the customer drawer. The drawer WRITES take no accountId.

// open-api getDeviceDetail (incl. classification + availableCommands). JSON String; null if not found.
export const STAFF_PROXY_TESTING_DEVICE_DETAIL = gql`
  query StaffProxyTestingDeviceDetail($deviceId: String!, $accountId: String) {
    staffProxyTestingDeviceDetail(deviceId: $deviceId, accountId: $accountId)
  }
`;

// open-api mdmDetails — GET_DEVICE_INFO asks the device itself (15s server cache; fresh bypasses).
// JSON String; null/empty when the device does not answer.
export const STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS = gql`
  query StaffProxyTestingDeviceMdmDetails(
    $deviceId: String!
    $command: String!
    $fresh: Boolean
    $accountId: String
  ) {
    staffProxyTestingDeviceMdmDetails(
      deviceId: $deviceId
      command: $command
      fresh: $fresh
      accountId: $accountId
    )
  }
`;

// open-api deviceActivityFeed, newest first. JSON String:
// [{ id, kind: 'audit'|'command'|'vendorOp', at, actorUserId, actorDisplay, operationKey, params, status }].
export const STAFF_PROXY_TESTING_DEVICE_ACTIVITY_FEED = gql`
  query StaffProxyTestingDeviceActivityFeed($deviceId: String!, $limit: Int, $accountId: String) {
    staffProxyTestingDeviceActivityFeed(deviceId: $deviceId, limit: $limit, accountId: $accountId)
  }
`;

// What staff did to this device through staff-app (staff_audit_log), newest first.
export const STAFF_TESTING_DEVICE_STAFF_ACTIONS = gql`
  query StaffTestingDeviceStaffActions($deviceId: String!, $limit: Int, $accountId: String) {
    staffTestingDeviceStaffActions(deviceId: $deviceId, limit: $limit, accountId: $accountId) {
      id
      time
      at
      tool
      submittedBy
      summary
      status
      detail
    }
  }
`;

// Bytes of an already-captured screenshot (a `takeScreens[].image` key on that device). A pure read —
// staff-api signs the file-cdn URL itself and returns base64, so the browser never sees a raw S3 key.
export const STAFF_PROXY_FETCH_DEVICE_SCREENSHOT = gql`
  query StaffProxyFetchDeviceScreenshot($accountId: String!, $deviceId: String!, $imageKey: String!) {
    staffProxyFetchDeviceScreenshot(accountId: $accountId, deviceId: $deviceId, imageKey: $imageKey) {
      imageBase64
      mimeType
    }
  }
`;

// My Testing Devices drawer › Identify Screen: open-api identifyScreen (START) — flashes the identify
// asset for ~30s. Testing-account devices, or (with accountId) a device of that customer account;
// 403 otherwise. JSON String result.
export const STAFF_PROXY_TESTING_IDENTIFY_SCREEN = gql`
  mutation StaffProxyTestingIdentifyScreen($deviceId: String!, $accountId: String) {
    staffProxyTestingIdentifyScreen(deviceId: $deviceId, accountId: $accountId)
  }
`;

// My Testing Devices drawer › Remote command history: open-api commandExecutions for the device, newest
// first. Testing-account devices only. JSON String:
// [{ _id, deviceId, command, value, status, result, type, createdAt, lastUpdatedDate }].
export const STAFF_PROXY_TESTING_COMMAND_EXECUTIONS = gql`
  query StaffProxyTestingCommandExecutions($deviceId: String!, $first: Int, $accountId: String) {
    staffProxyTestingCommandExecutions(deviceId: $deviceId, first: $first, accountId: $accountId)
  }
`;

// My Testing Devices drawer › Settings tab + OTA dialog: open-api updateDevice restricted server-side to
// an allowlist of setting keys (muted/orientation/scale/externalStorage + feature.{brightness, volume,
// scheduleOps*, lockdownPassword, playbackControl*, externalStoragePath, ota*}). `payload` is a JSON
// string; any other key is a 400 "setting not allowed: <key>". Testing-account devices, or (with
// accountId) a device of that customer account.
export const STAFF_PROXY_TESTING_UPDATE_DEVICE = gql`
  mutation StaffProxyTestingUpdateDevice($deviceId: String!, $payload: String!, $accountId: String) {
    staffProxyTestingUpdateDevice(deviceId: $deviceId, payload: $payload, accountId: $accountId)
  }
`;

// My Testing Devices drawer › Execute Remote Commands (and the OTA dialog's "check update" = forceOTA):
// open-api executeRemoteCommand for ONE device (testing account, or with accountId a device of that
// customer account). A real device command.
export const STAFF_PROXY_TESTING_EXECUTE_REMOTE_COMMAND = gql`
  mutation StaffProxyTestingExecuteRemoteCommand($deviceId: String!, $command: String!, $accountId: String) {
    staffProxyTestingExecuteRemoteCommand(deviceId: $deviceId, command: $command, accountId: $accountId)
  }
`;

// My Testing Devices drawer › Send RS232: the device team's serial ports + saved commands. JSON String
// {ports:[{_id,name,path,baudRate,dataBits,stopBits,parity,rtscts,xon,xoff,eol,encoding}],
//  commands:[{_id,name,value,eol,encoding}]}.
export const STAFF_TESTING_DEVICE_RS232_CONFIG = gql`
  query StaffTestingDeviceRs232Config($deviceId: String!, $accountId: String) {
    staffTestingDeviceRs232Config(deviceId: $deviceId, accountId: $accountId)
  }
`;

// My Testing Devices › Settings › Operational Schedule: the device account+team's saved Ops schedules
// (read-only). JSON-stringified [{_id, name, lastUpdatedDate}], newest first, max 100.
export const STAFF_TESTING_DEVICE_OPS_SCHEDULES = gql`
  query StaffTestingDeviceOpsSchedules($deviceId: String!, $accountId: String) {
    staffTestingDeviceOpsSchedules(deviceId: $deviceId, accountId: $accountId)
  }
`;

// Trigger a live screen capture, wait for the frame, and return WHERE it landed (a pointer — read the
// bytes with STAFF_PROXY_FETCH_DEVICE_SCREENSHOT). status: captured | unsupported_device | blocked |
// inconclusive | dispatch_failed | timeout. A real device command.
export const STAFF_PROXY_CAPTURE_DEVICE_SCREENSHOT = gql`
  mutation StaffProxyCaptureDeviceScreenshot($deviceId: String!, $accountId: String!, $sessionId: String) {
    staffProxyCaptureDeviceScreenshot(deviceId: $deviceId, accountId: $accountId, sessionId: $sessionId) {
      status
      reason
      capturedAt
      bucket
      imageKey
    }
  }
`;

// Cross-tenant device-troubleshoot session list — staff console (no args) + per-device drawer (deviceUuid).
// staff-api signs the cross-tenant (aid:'all') JWT; open-api returns sessions across all accounts.
export const STAFF_TROUBLESHOOT_SESSIONS = gql`
  query StaffTroubleshootSessions($deviceUuid: String) {
    staffTroubleshootSessions(deviceUuid: $deviceUuid) {
      sessionId
      accountId
      teamId
      deviceUUID
      deviceId
      deviceName
      title
      actor
      mode
      status
      region
      startedAt
      endedAt
      threadId
    }
  }
`;

// Read-only transcript of one session (any tenant). `payloadJson` is the event payload as a JSON string.
export const STAFF_TROUBLESHOOT_SESSION_TRANSCRIPT = gql`
  query StaffTroubleshootSessionTranscript($sessionId: String!) {
    staffTroubleshootSessionTranscript(sessionId: $sessionId) {
      seq
      ts
      type
      actor
      payloadJson
    }
  }
`;

// Start a LIVE staff device-troubleshoot session (staff-api → open-api startDeviceTroubleshootSession,
// mode:'staff', the operator is the actor + approver). Returns the capability handle the browser opens the
// device-agent-api SSE with — the Firebase token is never sent to device-agent-api.
export const STAFF_START_DEVICE_TROUBLESHOOT_SESSION = gql`
  mutation StartDeviceTroubleshootSession($deviceId: String!) {
    startDeviceTroubleshootSession(deviceId: $deviceId) {
      sessionId
      sessionToken
      streamUrl
      deviceName
      title
      actor
    }
  }
`;
